import api from './api';

export interface AdminStats {
  totalUsers: number;
  totalPartners: number;
  totalOrders: number;
  totalRevenue: number;
  totalTrucks: number;
  totalServices: number;
  totalOffers: number;
  totalReviews: number;
}

export interface AdminAnalysis {
  monthlyRevenue: Array<{ month: string; revenue: number }>;
  topPartners: Array<{ partnerId: number; companyName: string; totalOrders: number; totalRevenue: number }>;
  orderStatusDistribution: Array<{ status: string; count: number }>;
  userGrowth: Array<{ month: string; newUsers: number }>;
  partnerGrowth: Array<{ month: string; newPartners: number }>;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

class AdminService {
  async getDashboardStats(): Promise<AdminStats> {
    try {
      const response = await api.get('/admin/dashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      return {
        totalUsers: 0,
        totalPartners: 0,
        totalOrders: 0,
        totalRevenue: 0,
        totalTrucks: 0,
        totalServices: 0,
        totalOffers: 0,
        totalReviews: 0,
      };
    }
  }

  async getDashboardAnalysis(): Promise<AdminAnalysis> {
    try {
      const response = await api.get('/admin/analysis');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin analysis:', error);
      return {
        monthlyRevenue: [],
        topPartners: [],
        orderStatusDistribution: [],
        userGrowth: [],
        partnerGrowth: [],
      };
    }
  }

  async getUsers(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/users', {
      params: { page, limit }
    });
    return response.data;
  }

  async getPartners(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/partner-profiles', {
      params: { page, limit }
    });
    return response.data;
  }

  async getOrders(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/orders', {
      params: { page, limit }
    });
    return response.data;
  }

  async getTrucks(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/trucks', {
      params: { page, limit }
    });
    return response.data;
  }

  async getServices(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/services', {
      params: { page, limit }
    });
    return response.data;
  }

  async getOffers(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/offers', {
      params: { page, limit }
    });
    return response.data;
  }

  async getReviews(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/reviews', {
      params: { page, limit }
    });
    return response.data;
  }

  async getCategories(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/service-categories', {
      params: { page, limit }
    });
    return response.data;
  }

  async getTruckTypes(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/truck-types', {
      params: { page, limit }
    });
    return response.data;
  }

  async getPartnerPlans(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/partner-plans', {
      params: { page, limit }
    });
    return response.data;
  }

  async getPartnerSubscriptions(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/partner-subscriptions', {
      params: { page, limit }
    });
    return response.data;
  }

  async getLocations(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/locations', {
      params: { page, limit }
    });
    return response.data;
  }

  async getAddresses(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/addresses', {
      params: { page, limit }
    });
    return response.data;
  }

  async getFavorites(page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/favorites', {
      params: { page, limit }
    });
    return response.data;
  }

  // Search functionality
  async searchUsers(query: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/users', {
      params: { search: query, page, limit }
    });
    return response.data;
  }

  async searchPartners(query: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/partner-profiles', {
      params: { search: query, page, limit }
    });
    return response.data;
  }

  async searchOrders(query: string, page: number = 1, limit: number = 10): Promise<PaginatedResponse<any>> {
    const response = await api.get('/orders', {
      params: { search: query, page, limit }
    });
    return response.data;
  }

  // Bulk operations
  async bulkUpdateUserStatus(userIds: number[], isActive: boolean): Promise<any[]> {
    const promises = userIds.map(id => 
      api.patch(`/users/${id}`, { isActive })
    );
    const responses = await Promise.all(promises);
    return responses.map(res => res.data);
  }

  async bulkUpdatePartnerStatus(partnerIds: number[], isActive: boolean): Promise<any[]> {
    const promises = partnerIds.map(id => 
      api.patch(`/partner-profiles/${id}`, { isActive })
    );
    const responses = await Promise.all(promises);
    return responses.map(res => res.data);
  }

  async bulkUpdateOrderStatus(orderIds: number[], status: string): Promise<any[]> {
    const promises = orderIds.map(id => 
      api.patch(`/orders/${id}`, { status })
    );
    const responses = await Promise.all(promises);
    return responses.map(res => res.data);
  }

  // Export functionality
  async exportUsers(format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/admin/export/users', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }

  async exportOrders(format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/admin/export/orders', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }

  async exportPartners(format: 'csv' | 'json' = 'csv'): Promise<Blob> {
    const response = await api.get('/admin/export/partners', {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }
}

const adminService = new AdminService();

export default adminService;
